<template>
    <!-- Shopee cancel reconcile §11 F2/F3 (spec 2026-10-07-shopee-cancel-reconcile-design.md).
         OrdersNew.vue runs the census (S1) and hands the result in; this dialog
         shows it and runs the repair (S2): dry run -> result -> swal confirm ->
         real apply -> `repaired` (the page reloads its list). -->
    <el-dialog
        :visible="visible"
        custom-class="bo-shopee-census-dialog"
        :title="$t('shopee_census.title', { from: from, to: to })"
        :append-to-body="true"
        :close-on-click-modal="!busy"
        :close-on-press-escape="!busy"
        :show-close="!busy"
        width="900px"
        @close="$emit('update:visible', false)"
    >
        <!-- ============ Step 1: census result ============ -->
        <div v-if="step === 'list'" data-testid="census-list">
            <div class="d-flex flex-wrap align-items-center mb-3">
                <span class="mr-3 text-muted">{{ $t("shopee_census.total", { n: total }) }}</span>
                <el-tag
                    v-for="chip in chips"
                    :key="chip.cls"
                    :type="classTone(chip.cls)"
                    size="small"
                    class="mr-2 mb-1"
                    data-testid="census-chip"
                >{{ $t(classLabel(chip.cls)) }} {{ chip.n }}</el-tag>
                <span class="ml-auto d-flex align-items-center">
                    <span class="mr-2">{{ $t("shopee_census.show_all") }}</span>
                    <el-switch v-model="showAll" data-testid="census-show-all"></el-switch>
                </span>
            </div>

            <el-table
                ref="censusTable"
                :data="rows"
                row-key="bo_id"
                max-height="420"
                size="mini"
                header-row-class-name="thead-light"
                :empty-text="total ? $t('shopee_census.empty') : $t('shopee_census.empty_all')"
                @selection-change="onSelectionChange"
            >
                <el-table-column type="selection" width="36" :selectable="isSelectable"></el-table-column>
                <el-table-column :label="$t('shopee_census.col_order')" width="125">
                    <template slot-scope="{ row }">
                        <a href="#" class="font-weight-bold" data-testid="census-open-order" @click.prevent="openOrder(row)">{{ row.bo_id }}</a>
                        <div class="text-muted small">{{ row.order_sn }}</div>
                    </template>
                </el-table-column>
                <el-table-column :label="$t('shopee_census.col_bo_status')" width="95">
                    <template slot-scope="{ row }">{{ statusLabel(row.bo_status) }}</template>
                </el-table-column>
                <el-table-column :label="$t('shopee_census.col_platform_status_bo')" width="125">
                    <template slot-scope="{ row }">{{ row.platform_status_bo || "-" }}</template>
                </el-table-column>
                <el-table-column :label="$t('shopee_census.col_platform_status_now')" width="135">
                    <template slot-scope="{ row }">{{ row.platform_status_now || "-" }}</template>
                </el-table-column>
                <el-table-column :label="$t('shopee_census.col_packing')" width="70">
                    <template slot-scope="{ row }">{{ Number(row.packing_id) > 0 ? "#" + row.packing_id : "-" }}</template>
                </el-table-column>
                <el-table-column :label="$t('shopee_census.col_cancel_by')" width="85">
                    <template slot-scope="{ row }">
                        <div>{{ row.cancel_by || "-" }}</div>
                        <div v-if="Number(row.bo_cancel_user_id) > 0" class="text-muted small">
                            {{ $t("shopee_census.cancel_by_staff", { id: row.bo_cancel_user_id }) }}
                        </div>
                    </template>
                </el-table-column>
                <el-table-column :label="$t('shopee_census.col_class')" min-width="150">
                    <template slot-scope="{ row }">
                        <el-tag :type="classTone(row.class)" size="mini" class="bo-census-class">{{ $t(classLabel(row.class)) }}</el-tag>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <!-- ============ Step 2/3: dry run / real apply result ============ -->
        <div v-else data-testid="census-result">
            <h4 class="mb-3">{{ step === "dry" ? $t("shopee_census.dry_title") : $t("shopee_census.done_title") }}</h4>
            <div class="mb-2 font-weight-bold">
                {{ step === "dry" ? $t("shopee_census.will_apply", { n: shownResult.applied.length }) : $t("shopee_census.applied_n", { n: shownResult.applied.length }) }}
            </div>
            <ul v-if="shownResult.applied.length" class="bo-census-list mb-3" data-testid="census-applied">
                <li v-for="a in shownResult.applied" :key="'a' + a.bo_id">
                    <span class="font-weight-bold">#{{ a.bo_id }}</span>
                    <span class="text-muted">{{ a.order_sn }}</span>
                    · {{ $t(actionLabel(a.action)) }}:
                    {{ statusLabel(a.from_status) }} → {{ statusLabel(a.to_status) }}
                    <span v-if="a.platform_status_now" class="text-muted">({{ a.platform_status_now }})</span>
                </li>
            </ul>
            <div v-if="!shownResult.applied.length && step === 'dry'" class="text-muted mb-3">{{ $t("shopee_census.nothing_to_apply") }}</div>
            <template v-if="shownResult.skipped.length">
                <div class="mb-2 font-weight-bold">{{ $t("shopee_census.skipped_n", { n: shownResult.skipped.length }) }}</div>
                <ul class="bo-census-list mb-0" data-testid="census-skipped">
                    <li v-for="k in shownResult.skipped" :key="'s' + k.bo_id">
                        <span class="font-weight-bold">#{{ k.bo_id }}</span> · {{ skipText(k.reason) }}
                    </li>
                </ul>
            </template>
        </div>

        <div slot="footer" class="d-flex flex-wrap align-items-center">
            <template v-if="step === 'list'">
                <base-button
                    outline
                    type="default"
                    class="mr-auto"
                    :loading="csvBusy"
                    :disabled="csvBusy || busy"
                    data-testid="census-csv"
                    @click="downloadCsv"
                >
                    <i class="fa fa-download"></i> {{ $t("shopee_census.download_csv") }}
                </base-button>
                <span class="text-muted mr-3">{{ $t("shopee_census.selected_n", { n: selectedBoIds.length }) }}</span>
                <base-button type="secondary" :disabled="busy" @click="$emit('update:visible', false)">
                    {{ $t("shopee_census.close") }}
                </base-button>
                <base-button
                    type="primary"
                    :loading="busy"
                    :disabled="busy || !selectedBoIds.length"
                    data-testid="census-dry-run"
                    @click="dryRun"
                >
                    {{ $t("shopee_census.repair_dry") }}
                </base-button>
            </template>
            <template v-else-if="step === 'dry'">
                <base-button type="secondary" class="mr-auto" :disabled="busy" @click="backToList">
                    {{ $t("shopee_census.back") }}
                </base-button>
                <base-button
                    type="danger"
                    :loading="busy"
                    :disabled="busy || !dryIds.length"
                    data-testid="census-apply"
                    @click="confirmApply"
                >
                    {{ $t("shopee_census.repair_confirm_btn", { n: dryIds.length }) }}
                </base-button>
            </template>
            <template v-else>
                <base-button type="primary" class="ml-auto" @click="$emit('update:visible', false)">
                    {{ $t("shopee_census.close") }}
                </base-button>
            </template>
        </div>
    </el-dialog>
</template>

<script>
// Result + repair dialog of the "ตรวจสอบกับ Shopee" tool (admin only; the
// page decides who sees the button). Every rule lives in
// src/utils/shopeeCancelCensus.js; this file only wires it. The flow is pinned
// by tests/unit/ordersNewView.spec.js (stub-script, as this repo's jest cannot
// mount a .vue file).
import { Dialog, Table, TableColumn, Tag, Switch } from "element-ui";
import swal from "sweetalert2";
import { ShopeeService } from "@/resource";
import { apiErrorMessage } from "@/util/api-error";
import { STATUS_LABEL_KEYS } from "@/utils/ordersNew";
import {
    APPLY_MAX_IDS,
    actionLabel,
    appliedIds,
    applyBody,
    applyResult,
    censusParams,
    classLabel,
    classTone,
    csvFilename,
    selectable,
    selectedIds,
    skipReasonLabel,
    summaryChips,
    visibleRows
} from "@/utils/shopeeCancelCensus";

export default {
    name: "ShopeeCancelCensusDialog",
    components: {
        [Dialog.name]: Dialog,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Switch.name]: Switch
    },
    props: {
        visible: { type: Boolean, default: false },
        // S1 response: {time_from, time_to, total, summary, orders}.
        result: { type: Object, default: null },
        // The window the census ran on (YYYY-MM-DD) — CSV asks for the same.
        from: { type: String, default: "" },
        to: { type: String, default: "" }
    },
    data() {
        return {
            showAll: false,
            // Rows ticked in the table (el-table selection-change).
            selection: [],
            // 'list' (census) -> 'dry' (S2 dry run) -> 'done' (S2 real apply).
            step: "list",
            dryResult: null,
            doneResult: null,
            busy: false,
            csvBusy: false
        };
    },
    computed: {
        orders() {
            return this.result && Array.isArray(this.result.orders) ? this.result.orders : [];
        },
        total() {
            const n = this.result ? Number(this.result.total) : 0;
            return n > 0 ? n : this.orders.length;
        },
        rows() {
            return visibleRows(this.orders, this.showAll);
        },
        chips() {
            return summaryChips(this.result && this.result.summary);
        },
        selectedBoIds() {
            return selectedIds(this.selection);
        },
        // What the dry run says it WOULD repair = what the real apply sends.
        dryIds() {
            return appliedIds(this.dryResult);
        },
        shownResult() {
            return applyResult(this.step === "done" ? this.doneResult : this.dryResult);
        }
    },
    watch: {
        // A new census = a fresh dialog.
        result() {
            this.reset();
        }
    },
    methods: {
        reset() {
            this.showAll = false;
            this.selection = [];
            this.step = "list";
            this.dryResult = null;
            this.doneResult = null;
        },
        classLabel(cls) {
            return classLabel(cls);
        },
        classTone(cls) {
            return classTone(cls);
        },
        actionLabel(action) {
            return actionLabel(action);
        },
        isSelectable(row) {
            return !!row && selectable(row.class);
        },
        statusLabel(code) {
            const key = STATUS_LABEL_KEYS[Number(code)];
            return key ? this.$t(key) : String(code === undefined || code === null ? "-" : code);
        },
        skipText(reason) {
            return skipReasonLabel(reason, (k, p) => this.$t(k, p));
        },
        onSelectionChange(rows) {
            this.selection = Array.isArray(rows) ? rows.slice() : [];
        },
        // Order number -> the page opens that row's inline detail (if it is on
        // the current page) — see OrdersNew.vue#onCensusOpenOrder.
        openOrder(row) {
            const id = Number(row && row.bo_id);
            if (id > 0) this.$emit("open-order", id);
        },
        // The dry-run result is kept: confirmApply only needs "a dry run
        // exists" (its button shows on the dry step only, and the next dryRun
        // or a new census replaces it; the BE re-checks Shopee on apply anyway).
        backToList() {
            this.step = "list";
            // el-table drops its ticks when the list step re-renders: the
            // count and the repair button must not keep the old selection.
            this.selection = [];
        },
        errorSwal(titleKey, err) {
            return swal.fire({
                title: this.$t(titleKey),
                text: apiErrorMessage(err, (k) => this.$t(k)),
                icon: "error",
                confirmButtonClass: "btn btn-danger btn-fill",
                buttonsStyling: false
            });
        },
        // F3 step 1: S2 with dry_run: true for the ticked rows. Writes nothing.
        dryRun() {
            const ids = this.selectedBoIds;
            if (!ids.length || this.busy) return Promise.resolve(false);
            if (ids.length > APPLY_MAX_IDS) {
                swal.fire({ title: this.$t("shopee_census.repair_too_many", { max: APPLY_MAX_IDS }), icon: "warning" });
                return Promise.resolve(false);
            }
            this.busy = true;
            return Promise.resolve()
                .then(() => ShopeeService.CancelCensusApply(applyBody(ids, true)))
                .then(
                    (res) => {
                        this.busy = false;
                        this.dryResult = (res && res.data) || {};
                        this.step = "dry";
                        return true;
                    },
                    (err) => {
                        this.busy = false;
                        this.errorSwal("shopee_census.apply_error", err);
                        return false;
                    }
                );
        },
        // F3 step 2: confirm, then S2 for real with the ids the dry run would
        // repair; the page reloads its list (`repaired`).
        confirmApply() {
            const ids = this.dryIds;
            if (!ids.length || this.busy) return Promise.resolve(false);
            return swal.fire({
                title: this.$t("shopee_census.repair_confirm_title", { n: ids.length }),
                text: this.$t("shopee_census.repair_confirm_text"),
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                confirmButtonText: this.$t("shopee_census.repair_confirm_btn", { n: ids.length }),
                cancelButtonText: this.$t("cancel"),
                confirmButtonClass: "btn btn-danger btn-fill",
                cancelButtonClass: "btn btn-secondary btn-fill",
                buttonsStyling: false
            }).then((answer) => {
                // sweetalert2 v9 answers {value: true}; v10+ adds isConfirmed.
                const ok = !!answer && (answer.isConfirmed === true || (answer.value !== undefined && !!answer.value && !answer.dismiss));
                if (!ok || this.busy) return false;
                this.busy = true;
                return Promise.resolve()
                    .then(() => ShopeeService.CancelCensusApply(applyBody(ids, false)))
                    .then(
                        (res) => {
                            this.busy = false;
                            this.doneResult = (res && res.data) || {};
                            this.step = "done";
                            this.$emit("repaired", this.doneResult);
                            return true;
                        },
                        (err) => {
                            this.busy = false;
                            this.errorSwal("shopee_census.apply_error", err);
                            return false;
                        }
                    );
            });
        },
        // CSV of the same window. The API authenticates with the Bearer header
        // only, so this is an axios blob request (ShopeeService.CancelCensusCsv),
        // never window.open().
        downloadCsv() {
            if (this.csvBusy) return Promise.resolve(false);
            this.csvBusy = true;
            return Promise.resolve()
                .then(() => ShopeeService.CancelCensusCsv(censusParams(this.from, this.to)))
                .then(
                    (resp) => {
                        this.csvBusy = false;
                        this.saveBlob(resp && resp.data, csvFilename(this.from, this.to));
                        return true;
                    },
                    () => {
                        this.csvBusy = false;
                        swal.fire({ title: this.$t("shopee_census.csv_failed"), icon: "error" });
                        return false;
                    }
                );
        },
        // Same house download as ShopeeBulkSyncModal#saveBlob (object URL,
        // detached anchor, revoke). The BE already prefixes a UTF-8 BOM.
        saveBlob(data, filename) {
            if (!data) return;
            const blob = data instanceof Blob ? data : new Blob([data], { type: "text/csv;charset=utf-8" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        }
    }
};
</script>

<style>
/* Appended to <body>: unscoped, under the dialog's own class. */
.bo-shopee-census-dialog .el-dialog__body {
    padding-top: 8px;
}
.bo-shopee-census-dialog .bo-census-class {
    white-space: normal;
    height: auto;
    line-height: 1.4;
    padding-top: 2px;
    padding-bottom: 2px;
}
/* Verifier render 2026-10-07: columns fixed at 36+125+95+125+135+70+85 + class
   min 150 = 821px, inside the ~852px the 900px dialog leaves (910px of
   min-widths clipped the class column and broke cells mid-word). */
/* Non-repairable rows: no (disabled) checkbox at all. */
.bo-shopee-census-dialog td .el-checkbox.is-disabled {
    visibility: hidden;
}
.bo-shopee-census-dialog .bo-census-list {
    max-height: 260px;
    overflow-y: auto;
    padding-left: 1.25rem;
}
@media (max-width: 991.98px) {
    .bo-shopee-census-dialog {
        width: 96% !important;
    }
}
</style>
